import {
  Box,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import { Cancel, FileDownloadOutlined, Search } from "@mui/icons-material";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { EXCHANGE_TIMEZONE } from "../../dayjsSetup";
import { ControlButtons } from "./controlButtons";
import { AllActionsButtons } from "./allActionsButtons";
import { Notifications } from "./contexts/notifications";
import { useListedAlgo } from "./contexts/listAlgosContext";
import { StopSelectedButton } from "./stopSelectedButton";
import { useEffect, useMemo, useRef, useState, memo } from "react";
import { TraderAlgosDatagrid } from "./datagrids/tradersAlgoDatagrid";
import { TradesDatagrid } from "./datagrids/tradesDatagrid";
import { AreaSelector } from "./datagrids/shared/areaSelector";
import { BottomView, useBottomView } from "./atoms/bottomView";
import { useHideGhostbooks } from "./atoms/hideGhostbooks";

interface LazerTraderStrategyWrapperProps {}

/** Sized to sit in the 42px toolbar next to the action buttons. */
const viewToggleSx = { paddingY: 0, paddingX: 1.25, lineHeight: 1.8 };

const LazerTraderStrategyWrapperComponent: React.FC<
  LazerTraderStrategyWrapperProps
> = () => {
  const { addPreventClearAlgosRef } = useListedAlgo();
  const [bottomView, setBottomView] = useBottomView();
  const [hideGhostbooks, setHideGhostbooks] = useHideGhostbooks();
  const tradesApiRef = useGridApiRef();

  const [search, setSearch] = useState("");
  const quickFilterValues = useMemo(
    () => search.split(" ").filter(Boolean),
    [search]
  );

  const excludedRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    addPreventClearAlgosRef(excludedRef);
  }, [addPreventClearAlgosRef]);

  return (
    <Box height={"100%"}>
      <Box display="flex" flex={1} flexDirection="column" height={"100%"}>
        <Box
          flex={1}
          minHeight={0}
          display="flex"
          flexDirection="column"
          sx={{
            borderRadius: 0,
            boxShadow: "none",
            border: "none",
          }}
        >
          <Box
            width="100%"
            display="flex"
            alignItems="center"
            position="relative"
            height={42}
            flexShrink={0}
            padding={1}
          >
            {/* Left: everything that shapes what the grid below shows, kept on
                the same edge as the data it acts on. The area chips are the one
                item allowed to shrink — they scroll sideways in a narrow window
                rather than squeezing the search box or the view toggle. */}
            <Box display="flex" alignItems="center" gap={2} minWidth={0}>
              {/* Which dataset you are looking at comes first — the filters
                  after it only make sense in terms of that choice. */}
              <ToggleButtonGroup
                size="small"
                exclusive
                sx={{ flexShrink: 0 }}
                value={bottomView}
                onChange={(_, value: BottomView | null) => {
                  // Null when the active button is clicked again; an exclusive
                  // group has to keep one selected.
                  if (value !== null) {
                    setBottomView(value);
                  }
                }}
              >
                <ToggleButton value="algos" sx={viewToggleSx}>
                  Algos
                </ToggleButton>
                <ToggleButton value="trades" sx={viewToggleSx}>
                  Trades
                </ToggleButton>
              </ToggleButtonGroup>
              <AreaSelector />
              {/* Drives `quickFilterValues` on whichever grid is showing, so the
                  grids need no toolbar row of their own. Split on spaces the way
                  MUI's own quick filter input does — several terms are ANDed. */}
              <TextField
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search..."
                aria-label="Search"
                size="small"
                sx={{
                  width: 200,
                  flexShrink: 0,
                  "& .MuiInputBase-root": { height: 30 },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          aria-label="Clear search"
                          onClick={() => setSearch("")}
                          sx={{ marginRight: -0.75 }}
                        >
                          <Cancel fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
              {bottomView === "algos" && (
                <FormControlLabel
                  sx={{ flexShrink: 0, marginLeft: 0, whiteSpace: "nowrap" }}
                  control={
                    <Switch
                      size="small"
                      checked={hideGhostbooks}
                      onChange={(event) =>
                        setHideGhostbooks(event.target.checked)
                      }
                    />
                  }
                  label="Hide ghostbooks"
                  slotProps={{ typography: { variant: "body2" } }}
                />
              )}
              {/* Exports what the grid shows: the search, area filter and sort
                  above all carry into the file. The BOM is so Excel reads the
                  Japanese in the Information column as UTF-8. */}
              {bottomView === "trades" && (
                <Tooltip title="Export trades to CSV">
                  <IconButton
                    size="small"
                    aria-label="Export trades to CSV"
                    sx={{ flexShrink: 0 }}
                    onClick={() =>
                      tradesApiRef.current?.exportDataAsCsv({
                        fileName: `jepx-trades-${dayjs()
                          .tz(EXCHANGE_TIMEZONE)
                          .format("YYYY-MM-DD_HHmm")}`,
                        utf8WithBom: true,
                      })
                    }
                  >
                    <FileDownloadOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <Box flex={1} minWidth={24} />

            {/* The everyday delete, floated in the middle of the bar: the two
                spacers either side keep clear water between it and both the
                grid controls and the pair below. */}
            <Box display="flex" flexShrink={0}>
              <StopSelectedButton />
            </Box>

            {/* Holds a floor under the gap so a cramped window can never slide
                the buttons below within a near-miss of "Delete selected". */}
            <Box flex={1} minWidth={80} />

            {/* The two that cannot be taken back: every algo, and the kill
                switch for LazerTrader itself. */}
            <Box display="flex" alignItems="center" gap={1} flexShrink={0}>
              <AllActionsButtons />
              <ControlButtons />
            </Box>
          </Box>

          {/* A flex item defaults to `min-height: auto`, so without this a tall
              grid pushes the container open and runs off the panel instead of
              scrolling inside it. */}
          <Box flex={1} minHeight={0} display="flex" flexDirection="column">
            {bottomView === "trades" ? (
              <TradesDatagrid
                quickFilterValues={quickFilterValues}
                apiRef={tradesApiRef}
              />
            ) : (
              /* The grid owns algo selection through MUI's own row clicks, so
                 the click-outside clear must not fire for clicks landing in
                 here — it would wipe the selection a moment before the row
                 click rebuilt it, leaving only ever one row selected. */
              <Box
                ref={excludedRef}
                position="relative"
                height={"100%"}
                minHeight={0}
              >
                <TraderAlgosDatagrid quickFilterValues={quickFilterValues} />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <Box
        position="fixed"
        bottom="5%"
        left="50%"
        zIndex={9999}
        display={"flex"}
        alignContent={"center"}
        flexDirection={"column-reverse"}
      >
        <Notifications />
      </Box>
    </Box>
  );
};

export const LazerTraderStrategyWrapper = memo(
  LazerTraderStrategyWrapperComponent
);
